import { io, Socket } from 'socket.io-client';

class SocketService {
  private socket: Socket | null = null;
  private token: string | null = null;

  connect(token: string) {
    // Si déjà connecté avec le même token, ne pas reconnecter
    if (this.socket?.connected && this.token === token) return;
    
    // Si connecté avec un token différent, déconnecter d'abord
    if (this.socket?.connected && this.token !== token) {
      this.disconnect();
    }
    
    this.token = token;
    
    // Use environment-based URL for Socket.io connection
    const socketUrl = process.env.NODE_ENV === 'production' 
      ? 'https://bookshare-xp2f.onrender.com' 
      : 'http://localhost:5000';
    
    console.log('🔌 Connecting to Socket.io:', socketUrl);
    this.socket = io(socketUrl, {
      auth: { token }
    });

    this.socket.on('connect', () => {
      console.log('✅ Socket.io connecté');
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Erreur de connexion Socket.io:', error);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Socket.io déconnecté:', reason);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  joinChatRoom(bookRequestId: string) {
    console.log('🚪 [Client] Joining chat room:', { 
      bookRequestId, 
      connected: this.socket?.connected,
      socketId: this.socket?.id 
    });
    if (this.socket) {
      this.socket.emit('join_chat_room', { bookRequestId });
    } else {
      console.error('❌ [Client] Socket not connected for joinChatRoom');
    }
  }

  leaveChatRoom(bookRequestId: string) {
    if (this.socket) {
      this.socket.emit('leave_chat_room', { bookRequestId });
    }
  }

  sendMessage(bookRequestId: string, content: string) {
    console.log('📤 [Client] Sending message via Socket.io:', { 
      bookRequestId, 
      content, 
      connected: this.socket?.connected,
      socketId: this.socket?.id
    });
    if (this.socket) {
      this.socket.emit('send_message', { bookRequestId, content });
    } else {
      console.error('❌ [Client] Socket not connected');
    }
  }

  markMessagesAsRead(bookRequestId: string) {
    console.log('📖 [Client] Marking messages as read:', bookRequestId, 'connected:', this.socket?.connected);
    if (this.socket) {
      this.socket.emit('mark_messages_read', { bookRequestId });
    } else {
      console.error('❌ [Client] Socket not connected for markMessagesAsRead');
    }
  }

  onNewMessage(callback: (message: any) => void) {
    if (this.socket) {
      this.socket.on('new_message', (message) => {
        console.log('📨 [Client] new_message event received:', message);
        callback(message);
      });
    }
  }

  onChatHistory(callback: (messages: any[]) => void) {
    if (this.socket) {
      this.socket.on('chat_history', callback);
    }
  }

  onJoinedRoom(callback: (data: any) => void) {
    if (this.socket) {
      this.socket.on('joined_room', callback);
    }
  }

  onError(callback: (error: any) => void) {
    if (this.socket) {
      this.socket.on('error', callback);
    }
  }

  onMessagesMarkedRead(callback: (data: any) => void) {
    if (this.socket) {
      this.socket.on('messages_marked_read', callback);
    }
  }

  onChatAccessGranted(callback: (payload: any) => void) {
    if (this.socket) {
      this.socket.on('chat_access_granted', callback);
    }
  }

  removeChatAccessGrantedListener() {
    if (this.socket) {
      this.socket.off('chat_access_granted');
    }
  }

  removeAllListeners() {
    if (this.socket) {
      this.socket.removeAllListeners();
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

const socketService = new SocketService();
export default socketService;
